import { useMemo, useState } from "react";

import { toast } from "sonner";

import { ClearSearchButton, SearchUser } from "@/components/common";
import { MODALITIES_COLORS } from "@/features/dashboard/constants/modalities-chart-config";
import { formatLocalDate } from "@/features/dashboard/shell/range-utils";
import { useFilteredUsers } from "@/features/imagings/hooks/useImaging";
import { downloadImagingExcel } from "@/features/imagings/services/imagingService";
import { DataSection } from "@/features/reports/components/business/DataSection";
import { FiltersSection } from "@/features/reports/components/business/FiltersSection";
import { businessTableColumns } from "@/features/reports/components/business/tableColumns";
import { useImagingMetricsDashboard } from "@/features/reports/hooks/useImagingMetricsDashboard";
import type { User } from "@/features/users/schemas/user";
import { useDateFilter } from "@/shared/hooks/useDateFilter";
import { useDebounce } from "@/shared/hooks/useDebounce";
import { useModalities } from "@/shared/hooks/useModalities";
import { usePersistentState } from "@/shared/hooks/usePersistentState";
import { getModalityNameByAbbr } from "@/shared/schemas/modalities";

/** Paleta concreta (sin CSS vars) para distinguir modalidades en el pie */
const DISTINCT_MODALITY_COLORS = MODALITIES_COLORS.filter(
  (color) => color.startsWith("hsl(")
);

/**
 * Página de reportes de negocio (métricas desde imaging/metrics/dashboard)
 */
export default function Business(): React.JSX.Element {
  const [search, setSearch] = usePersistentState("business-report-search", "");
  const [selectedUser, setSelectedUser] = usePersistentState<User | null>(
    "business-report-user",
    null
  );
  const [showFilters, setShowFilters] = usePersistentState(
    "business-report-show-filters",
    false
  );
  const [showData, setShowData] = usePersistentState("business-report-show-data", false);

  const [loadingExcel, setLoadingExcel] = useState(false);

  const debouncedSearch = useDebounce(search, 500);

  const {
    dateFilter,
    showCustomDate,
    handleDateFilterChange,
    handleCustomDateChange,
    getPeriodText,
  } = useDateFilter();

  const {
    data: filteredUsers,
    error: errorFilteredUsers,
    isLoading: isLoadingFilteredUsers,
  } = useFilteredUsers(debouncedSearch);

  const dateFrom = formatLocalDate(dateFilter.startDate || new Date());
  const dateTo = formatLocalDate(dateFilter.endDate || new Date());

  const metricsParams =
    selectedUser && showData
      ? {
          user_id: selectedUser.uid,
          date_from: dateFrom,
          date_to: dateTo,
        }
      : null;

  const {
    data: metrics,
    isLoading: isLoadingMetrics,
    error: errorMetrics,
  } = useImagingMetricsDashboard(metricsParams, {
    enabled: !!selectedUser && showData,
  });

  const {
    data: modalitiesCatalog,
    isLoading: isLoadingModalities,
    error: errorModalities,
  } = useModalities();

  const handleUserSelect = (user: User) => {
    setShowData(false);
    setSelectedUser(user);
    setShowFilters(true);
  };

  const handleApplyFilters = () => {
    if (selectedUser) {
      setShowData(true);
      return;
    }

    setShowData(false);
  };

  const handleClearSearch = () => {
    setSearch("");
    setSelectedUser(null);
    setShowFilters(false);
    setShowData(false);
  };

  /**
   * Filas del pie/tabla + chartConfig con colores distintos por índice
   * y labels "DX — Rayos X" para no confundir DX/CR.
   */
  const { filteredData, chartConfig } = useMemo(() => {
    const byModality = metrics?.by_modality ?? [];
    const catalog = modalitiesCatalog ?? [];

    const config: Record<string, { label: string; color: string }> = {};
    const palette =
      DISTINCT_MODALITY_COLORS.length > 0
        ? DISTINCT_MODALITY_COLORS
        : MODALITIES_COLORS;

    const data = byModality.map((item, index) => {
      const color = palette[index % palette.length] ?? "hsl(200, 70%, 50%)";
      const displayName = getModalityNameByAbbr(catalog, item.modality, "es");
      const label =
        displayName && displayName !== item.modality
          ? `${item.modality} — ${displayName}`
          : item.modality;

      config[item.modality] = { label, color };

      return {
        modality: item.modality,
        amount: item.count,
        color,
        percentage: Number(item.percentage.toFixed(2)),
      };
    });

    return { filteredData: data, chartConfig: config };
  }, [metrics?.by_modality, modalitiesCatalog]);

  const handleDownloadExcel = async () => {
    try {
      setLoadingExcel(true);
      // Calendario local — no toISOString() (desplaza el día en zonas UTC±)
      const dateInit = formatLocalDate(dateFilter.startDate || new Date()).replace(
        /-/g,
        ""
      );
      const dateEnd = formatLocalDate(dateFilter.endDate || new Date()).replace(
        /-/g,
        ""
      );

      await downloadImagingExcel(
        {
          institution_id: selectedUser?.uid || "",
          date_init: dateInit || "",
          date_end: dateEnd || "",
        },
        `${selectedUser?.name} - ${dateFilter.startDate?.toLocaleDateString(
          "es-ES"
        )} - ${dateFilter.endDate?.toLocaleDateString("es-ES")}.xlsx`
      );
    } catch (error) {
      console.error(error);
      toast.error("Error al descargar el archivo");
    } finally {
      setLoadingExcel(false);
    }
  };

  return (
    <div className="space-y-6">
      <div className="space-y-6">
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
          <div>
            <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100">
              Reporte de Negocio
            </h1>
            <p className="text-gray-600 dark:text-gray-400">
              Análisis de datos de negocio por modalidad
            </p>
          </div>
        </div>

        <div className="space-y-4">
          <SearchUser
            search={search}
            title="Buscar institución o médico"
            onSearchChange={setSearch}
            filteredUsers={filteredUsers}
            isLoadingFilteredUsers={isLoadingFilteredUsers}
            errorFilteredUsers={errorFilteredUsers}
            selectedUser={selectedUser}
            onUserSelect={handleUserSelect}
            debouncedSearch={debouncedSearch}
          />

          {(search || selectedUser || showFilters || showData) && (
            <div className="flex justify-end">
              <ClearSearchButton onClear={handleClearSearch} className="text-sm" />
            </div>
          )}
        </div>

        {showFilters && (
          <FiltersSection
            selectedUser={selectedUser}
            dateFilter={dateFilter}
            showCustomDate={showCustomDate}
            onDateFilterChange={handleDateFilterChange}
            onCustomDateChange={handleCustomDateChange}
            onApplyFilters={handleApplyFilters}
            setShowData={setShowData}
          />
        )}

        {showData && (
          <DataSection
            handleDownloadExcel={handleDownloadExcel}
            loadingExcel={loadingExcel}
            filteredData={filteredData}
            totalStudies={metrics?.total_studies ?? 0}
            errorMetrics={errorMetrics}
            errorChartModalitiesConfig={errorModalities}
            isLoading={isLoadingMetrics || isLoadingModalities}
            getPeriodText={getPeriodText}
            chartConfig={chartConfig}
            columns={businessTableColumns}
            institutionName={selectedUser?.name || ""}
          />
        )}
      </div>
    </div>
  );
}
